import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { fetchEgliseInfo } from "../../services/aspirantService";

function rowId(a) {
  const raw = a?.aspirant_id ?? a?.id ?? a?.user_id;
  return raw != null ? String(raw) : "undefined";
}

function calcAge(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? `${age} ans` : "—";
}

export default function AspirantTable({
  title,
  icon,
  aspirants = [],
  checkedAspirants = {},
  singleSelect = false,
  egliseId: egliseIdProp,
  userRole,
  fallbackTotals,
  onPick,
  onReinscrire,
  showActionColumn = true, // Par défaut, afficher la colonne Action
  hideAgeAndPhone = false, // Nouvelle prop pour masquer les colonnes âge et téléphone
  showDistrictAndEglise = false, // Nouvelle prop pour afficher les colonnes district et eglise
  externalProgress = null, // Progression externe (pour les cas où on charge depuis plusieurs églises)
  externalLoadingProgress = false, // État de chargement externe
}) {
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // autonomie : récupère egliseId si non fourni
  const [egliseId, setEgliseId] = useState(egliseIdProp ?? null);

  // progression
  const [progress, setProgress] = useState({}); // id -> {percent, done, total, remaining, label}
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [progressError, setProgressError] = useState(null);
  
  // Utiliser la progression externe si fournie
  const effectiveProgress = externalProgress || progress;
  const effectiveLoadingProgress = externalProgress !== null ? externalLoadingProgress : loadingProgress;

  const short = useMemo(() => {
    const t = String(title || "").toLowerCase();
    if (t.includes("chef")) return "CG";
    if (t.includes("senior") || t.includes("syl")) return "SYL";
    return "CG";
  }, [title]);

  // suivre la prop egliseId
  useEffect(() => {
    if (egliseIdProp && egliseIdProp !== egliseId) setEgliseId(egliseIdProp);
  }, [egliseIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // aller chercher egliseId si absent
  useEffect(() => {
    if (egliseId != null) return;
    let cancelled = false;
    (async () => {
      try {
        const eg = await fetchEgliseInfo();
        if (!cancelled && eg?.eglise?.id) {
          setEgliseId(Number(eg.eglise.id));
          return;
        }
      } catch {}
      try {
        const me = await api.get("/me");
        const idFromMe =
          me?.data?.eglise?.id ||
          me?.data?.user?.eglise_id ||
          me?.data?.user?.eglise?.id ||
          null;
        if (!cancelled && idFromMe) setEgliseId(Number(idFromMe));
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [egliseId]);

  // charger la progression (seulement si pas de progression externe)
  useEffect(() => {
    if (externalProgress !== null) return; // Ne pas charger si progression externe fournie
    if (!egliseId || !short) return;
    let cancelled = false;
    setLoadingProgress(true);
    setProgressError(null);

    (async () => {
      try {
        const res = await api.get(`/districtStats/eglise/${egliseId}/progress/${short}`);
        const data = Array.isArray(res.data) ? res.data : [];

        const map = {};
        for (const r of data) {
          const done = Number(r.done ?? 0) || 0;
          const total = Number(r.total ?? 0) || 0;

          const remaining =
            r.remaining != null ? Number(r.remaining) : total > 0 ? Math.max(0, total - done) : 0;

          const percentRaw =
            r.progress != null ? Number(r.progress) : total > 0 ? (done / total) * 100 : 0;

          const percent = Number.isFinite(percentRaw)
            ? Math.max(0, Math.min(100, Math.round(percentRaw)))
            : 0;

          const label = `${done}/${total}`;

          const entry = { percent, done, total, remaining, label };

          const k1 = r.aspirant_id != null ? String(r.aspirant_id) : null;
          const k2 = r.user_id != null ? String(r.user_id) : null;
          const k3 = r.id != null ? String(r.id) : null;
          if (k1) map[k1] = entry;
          if (k2) map[k2] = entry;
          if (k3) map[k3] = entry;
        }

        if (!cancelled) {
          setProgress(map);
          setLoadingProgress(false);
        }
      } catch (err) {
        if (!cancelled) {
          setProgress({});
          setLoadingProgress(false);
          setProgressError("Impossible de charger la progression.");
          // console.error(err?.response?.data || err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [egliseId, short, externalProgress]);

  // pagination
  const totalPages = Math.max(1, Math.ceil(aspirants.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentAspirants = aspirants.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // UI progression améliorée
  const renderProgress = (a) => {
    const k = rowId(a);
    const p = effectiveProgress[k] || { percent: 0, label: "0/—" };
    const percent = Math.max(0, Math.min(100, Number(p.percent) || 0));

    // Skeleton pendant le chargement
    if (effectiveLoadingProgress) {
      return (
        <div>
          <div className="placeholder-glow mb-1">
            <span className="placeholder col-4" />
          </div>
          <div className="progress" style={{ height: 18 }}>
            <div className="progress-bar bg-light" style={{ width: "30%" }} />
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="small text-muted mb-1 d-none d-md-flex align-items-center gap-2">
          <span className="badge bg-light text-dark">{p.label || `${p.done || 0}/${p.total || 0}`} </span>
          {progressError && (
            <span className="text-danger small" title={progressError}>
              ⚠︎
            </span>
          )}
        </div>
        <div className="progress rounded-pill" style={{ height: 18, position: "relative" }}>
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "#fff",
              textShadow: "0 0 2px rgba(0,0,0,0.6)",
              pointerEvents: "none",
            }}
          >
            {percent}%
          </span>
          <div
            className="progress-bar bg-primary"
            role="progressbar"
            style={{ width: `${percent}%`, transition: "width .35s ease" }}
            aria-valuenow={percent}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
      </div>
    );
  };

  const clickName = (a) => {
    if (typeof onPick === "function") onPick(a);
  };

  // Fonction pour rendre les boutons d'action
  const renderAction = (a) => {
    const k = rowId(a);
    const p = effectiveProgress[k] || { percent: 0 };
    const percent = Math.max(0, Math.min(100, Number(p.percent) || 0));
    const hasMatricule = !!a.matricule;
    const progressReached = percent >= 90;

    // Si pas de matricule (en attente) -> bouton Réinscrire
    if (!hasMatricule) {
      return (
        <button
          className="btn btn-sm btn-warning"
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onReinscrire === "function") {
              onReinscrire(a);
            }
          }}
          title="Réinscrire l'élève"
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Réinscrire
        </button>
      );
    }

    // Si matricule existe ET progression >= 90% -> bouton Soumettre à l'admin
    if (hasMatricule && progressReached) {
      return (
        <button
          className="btn btn-sm btn-success"
          onClick={(e) => {
            e.stopPropagation();
            // TODO: Implémenter l'action de soumission à l'admin
            console.log("Soumettre à l'admin:", a);
          }}
          title="Soumettre à l'administrateur"
        >
          <i className="bi bi-send me-1"></i>
          Soumettre à l'admin
        </button>
      );
    }

    // Sinon, pas de bouton
    return <span className="text-muted">—</span>;
  };

  return (
    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 16 }}>
      <div className="card-header bg-white d-flex justify-content-between align-items-center"
           style={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
        <h5 className="mb-0 d-flex align-items-center gap-2">
          {icon} <span className="text-primary1">
            <span className="d-inline d-md-none">
              {title === "Chef Guide" ? "CG" : title === "Senior Youth Leader" ? "SYL" : title}
            </span>
            <span className="d-none d-md-inline">{title}</span>
          </span>
        </h5>
        <span className="badge bg-dark-subtle text-dark-emphasis px-3 py-2 rounded-pill">
          Total : {aspirants.length}
        </span>
      </div>

      <div className="card-body">
        {aspirants.length === 0 ? (
          <div className="text-center py-5">
            <div className="display-6 mb-2">🗂️</div>
            <p className="text-muted mb-0">Aucun élève {title} trouvé.</p>
          </div>
        ) : (
          <>
            {/* Version desktop : tableau */}
            <div className="table-responsive d-none d-md-block">
              <table
                className="table table-striped table-hover align-middle table-sm mb-0"
                style={{ width: "100%", borderRadius: 12, overflow: "hidden" }}
              >
                <colgroup>
                  <col style={{ width: showActionColumn ? "20%" : "25%" }} />
                  <col style={{ width: showActionColumn ? "10%" : "12%" }} />
                  {showDistrictAndEglise && <col style={{ width: showActionColumn ? "12%" : "15%" }} />}
                  {showDistrictAndEglise && <col style={{ width: showActionColumn ? "12%" : "15%" }} />}
                  {!hideAgeAndPhone && <col style={{ width: showActionColumn ? "10%" : "12%" }} />}
                  {!hideAgeAndPhone && <col style={{ width: showActionColumn ? "12%" : "14%" }} />}
                  <col style={{ width: showActionColumn ? "30%" : "33%" }} />
                  {showActionColumn && <col style={{ width: "15%" }} />}
                </colgroup>

                <thead
                  className="table-dark"
                  style={{
                    position: "sticky",
                    backgroundColor:"rgb(13, 110, 253) !important",
                    top: 0,
                    zIndex: 2,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                  }}
                >
                  <tr>
                    <th>Nom</th>
                    <th className="matricule-landscape">Matricule</th>
                    {showDistrictAndEglise && <th>District</th>}
                    {showDistrictAndEglise && <th>Église</th>}
                    {!hideAgeAndPhone && <th>Âge</th>}
                    {!hideAgeAndPhone && <th>Téléphone</th>}
                    <th>Progression</th>
                    {showActionColumn && <th className="text-center">Action</th>}
                  </tr>
                </thead>

                <tbody>
                  {currentAspirants.map((a) => {
                    const k = rowId(a);
                    return (
                      <tr key={k} className="align-middle">
                        <td
                          className="fw-semibold text-primary"
                          style={{ cursor: "pointer" }}
                          onClick={() => clickName(a)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") clickName(a);
                          }}
                          role="button"
                          tabIndex={0}
                          title="Voir les exigences"
                        >
                          {a.name}
                        </td>
                        <td className="matricule-landscape">
                          {a.matricule ? (
                            <span className="badge bg-success text-white">{a.matricule}</span>
                          ) : (
                            <div>
                              <span className="badge bg-warning text-dark">En attente</span>
                              {a.rejection_comment && (
                                <div className="mt-1">
                                  <small className="text-danger" style={{ fontSize: "0.75rem" }}>
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    {a.rejection_comment}
                                  </small>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        {showDistrictAndEglise && (
                          <td>
                            {a.district_name || "—"}
                          </td>
                        )}
                        {showDistrictAndEglise && (
                          <td>
                            {a.eglise_name || "—"}
                          </td>
                        )}
                        {!hideAgeAndPhone && <td>{calcAge(a.birth_date)}</td>}
                        {!hideAgeAndPhone && <td>{a.phone || "—"}</td>}
                        <td className="pe-md-2">{renderProgress(a)}</td>
                        {showActionColumn && <td className="text-center">{renderAction(a)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Version mobile : cartes */}
            <div className="d-md-none">
              {currentAspirants.map((a) => {
                const k = rowId(a);
                const p = effectiveProgress[k] || { percent: 0, label: "0/—" };
                const percent = Math.max(0, Math.min(100, Number(p.percent) || 0));
                return (
                  <div key={k} className="card mb-3 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 
                            className="fw-semibold mb-1 text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => clickName(a)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") clickName(a);
                            }}
                            role="button"
                            tabIndex={0}
                            title="Voir les exigences"
                          >
                            {a.name}
                          </h6>
                          <div className="mb-2">
                            <small className="text-muted d-block">Matricule:</small>
                            {a.matricule ? (
                              <span className="badge bg-success text-white">{a.matricule}</span>
                            ) : (
                              <div>
                                <span className="badge bg-warning text-dark">En attente</span>
                                {a.rejection_comment && (
                                  <div className="mt-1">
                                    <small className="text-danger" style={{ fontSize: "0.75rem" }}>
                                      <i className="bi bi-exclamation-triangle me-1"></i>
                                      {a.rejection_comment}
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {showDistrictAndEglise && (
                        <>
                          <div className="mb-2">
                            <small className="text-muted d-block">District:</small>
                            <span>{a.district_name || "—"}</span>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted d-block">Église:</small>
                            <span>{a.eglise_name || "—"}</span>
                          </div>
                        </>
                      )}
                      
                      {!hideAgeAndPhone && (
                        <>
                          <div className="mb-2">
                            <small className="text-muted d-block">Âge:</small>
                            <span>{calcAge(a.birth_date)}</span>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted d-block">Téléphone:</small>
                            <span>{a.phone || "—"}</span>
                          </div>
                        </>
                      )}
                      
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Progression:</small>
                        {effectiveLoadingProgress ? (
                          <div>
                            <div className="placeholder-glow mb-1">
                              <span className="placeholder col-4" />
                            </div>
                            <div className="progress" style={{ height: 18 }}>
                              <div className="progress-bar bg-light" style={{ width: "30%" }} />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">{p.label || `${p.done || 0}/${p.total || 0}`}</small>
                              {progressError && (
                                <small className="text-danger" title={progressError}>⚠︎</small>
                              )}
                            </div>
                            <div className="progress rounded-pill" style={{ height: 18, position: "relative" }}>
                              <span
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#fff",
                                  textShadow: "0 0 2px rgba(0,0,0,0.6)",
                                  pointerEvents: "none",
                                }}
                              >
                                {percent}%
                              </span>
                              <div
                                className="progress-bar bg-primary"
                                role="progressbar"
                                style={{ width: `${percent}%`, transition: "width .35s ease" }}
                                aria-valuenow={percent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      {showActionColumn && (
                        <div className="mt-2">
                          {renderAction(a)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Précédent
                </button>
                <span className="text-muted small">Page {currentPage} sur {totalPages}</span>
                <button
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

